import { X } from 'lucide-react';
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import styles from './DockedPanel.module.css';

// The "docked, non-modal" side panel (ADR 0061): it sits beside the page it belongs to, so the list stays in view and
// in reach — unlike Drawer, which covers the page with a scrim. From the tablet width up it is a sticky column; below that
// it flows in the page where it is placed. Esc closes it while focus is inside, and focus goes back to what opened it.

interface DockedPanelProps {
  readonly title: string;
  readonly sub?: string;
  readonly onClose: () => void;
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}

export function DockedPanel({ title, sub, onClose, footer, children }: DockedPanelProps): ReactNode {
  const titleId = useId();
  const panelRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  function onKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    // A pop-up opened from inside the panel (a select, a menu) closes first.
    if (document.querySelector('[data-popover]')) return;
    event.stopPropagation();
    onClose();
  }

  return (
    <aside ref={panelRef} className={styles.panel} role="region" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKeyDown}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          {sub !== undefined && <p className={styles.sub}>{sub}</p>}
        </div>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          <X size={16} aria-hidden />
        </button>
      </header>
      <div className={styles.body}>{children}</div>
      {footer !== undefined && <footer className={styles.footer}>{footer}</footer>}
    </aside>
  );
}
